import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { ArrowsClockwise, Plus, Trash } from '@phosphor-icons/react';
import { Modal, Field } from './Extensions';
import { useToast } from '@/hooks/use-toast';

const Trunks = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/didww/trunks');
        setItems(r.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const sync = async () => {
        try {
            const r = await api.post('/didww/trunks/sync');
            toast({ title: 'Sync OK', description: `+${r.data.inserted}, ✎${r.data.updated}` });
            load();
        } catch (e) {
            toast({ title: 'Sync selhal', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post('/didww/trunks', editing);
            setEditing(null);
            load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const del = async (id) => {
        if (!window.confirm('Smazat trunk?')) return;
        await api.delete(`/didww/trunks/${id}`);
        load();
    };

    return (
        <>
            <PageHeader
                eyebrow="SIP"
                title="SIP Trunky"
                description="Příchozí (Voice IN) SIP trunky z DIDWW. Synchronizujte ze svého DIDWW účtu nebo vytvořte lokální záznam."
                actions={
                    <div className="flex gap-2">
                        <button onClick={sync} data-testid={TID.didwwSyncTrunks}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <ArrowsClockwise size={14} weight="bold" />Sync DIDWW
                        </button>
                        <button onClick={() => setEditing({ name: '', capacity_limit: 10, transport: 'udp', default_dst: '' })}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            <Plus size={14} weight="bold" />Nový trunk
                        </button>
                    </div>
                }
            />

            <Card>
                {loading ? <div>Načítání…</div> : items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné trunky.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]"
                                style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Název</th>
                                <th className="text-left">Default destination</th>
                                <th className="text-center">Kapacita</th>
                                <th className="text-center">Transport</th>
                                <th className="text-center">Zdroj</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((t) => (
                                <tr key={t.id} className="border-t"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2">{t.name}</td>
                                    <td className="font-mono text-xs">{t.default_dst || '—'}</td>
                                    <td className="text-center font-mono">{t.capacity_limit || '—'}</td>
                                    <td className="text-center font-mono uppercase text-xs">{t.transport}</td>
                                    <td className="text-center">
                                        <StatusPill status={t.source === 'didww' ? 'info' : 'neutral'} label={t.source} />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => del(t.id)}
                                                className="p-1 hover:bg-[var(--bg-surface-hover)]"
                                                data-testid={`trunk-delete-${t.id}`}>
                                            <Trash size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title="Nový SIP Trunk" onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <Field label="Název" value={editing.name}
                               onChange={(v) => setEditing({ ...editing, name: v })} required />
                        <Field label="Default destination (SIP URI)" value={editing.default_dst || ''}
                               onChange={(v) => setEditing({ ...editing, default_dst: v })} mono />
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Kapacita (souběh)</label>
                            <input type="number" value={editing.capacity_limit || 10}
                                   onChange={(e) => setEditing({ ...editing, capacity_limit: parseInt(e.target.value, 10) })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Transport</label>
                            <select value={editing.transport || 'udp'}
                                    onChange={(e) => setEditing({ ...editing, transport: e.target.value })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                <option value="udp">UDP</option>
                                <option value="tcp">TCP</option>
                                <option value="tls">TLS</option>
                            </select>
                        </div>
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)}
                                    className="px-3 py-2 text-xs font-bold uppercase border"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit"
                                    className="px-3 py-2 text-xs font-bold uppercase"
                                    style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default Trunks;
